import { DateTime } from 'luxon';

/**
 * Represents a Firestore Timestamp object
 */
export interface FirestoreTimestamp {
  seconds: number;
  nanoseconds?: number;
  toDate: () => Date;
}

/**
 * Union type for date inputs that can be normalized
 */
export type DateInput = Date | FirestoreTimestamp | string | number | null | undefined;

/**
 * Normalizes various date input types to a Date object or null
 * @param dateInput - The date input to normalize
 * @returns Date object or null if invalid
 */
export function normalizeToDate(dateInput: DateInput): Date | null {
  if (!dateInput) return null;

  try {
    // Handle Date objects
    if (dateInput instanceof Date) {
      return isNaN(dateInput.getTime()) ? null : dateInput;
    }

    // Handle Firestore Timestamp objects with toDate method
    if (typeof dateInput === 'object' && 'toDate' in dateInput && typeof dateInput.toDate === 'function') {
      return dateInput.toDate();
    }

    // Handle Firestore Timestamp-like objects with seconds property
    if (typeof dateInput === 'object' && 'seconds' in dateInput && typeof dateInput.seconds === 'number') {
      return new Date(dateInput.seconds * 1000);
    }

    // Handle string or number timestamps
    if (typeof dateInput === 'string' || typeof dateInput === 'number') {
      const date = new Date(dateInput);
      return isNaN(date.getTime()) ? null : date;
    }

    // Fallback - try to convert to Date
    const date = new Date(dateInput as any);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/**
 * Formats a date as a relative time string (e.g., "2h ago", "3d ago")
 * @param dateInput - The date to format
 * @returns Formatted relative time string
 */
export function formatRelativeTime(dateInput: DateInput): string {
  const date = normalizeToDate(dateInput);
  
  if (!date) return 'Never';

  const now = DateTime.now();
  const inputDate = DateTime.fromJSDate(date);
  
  const diff = now.diff(inputDate, ['days', 'hours', 'minutes']);
  
  // Less than 1 hour ago
  if (diff.hours < 1) {
    return 'Recently';
  }
  
  // Less than 24 hours ago
  if (diff.days < 1) {
    return `${Math.floor(diff.hours)}h ago`;
  }
  
  // Less than 7 days ago
  if (diff.days < 7) {
    return `${Math.floor(diff.days)}d ago`;
  }
  
  // More than a week ago - show formatted date
  return inputDate.toLocaleString(DateTime.DATE_SHORT);
}

/**
 * Formats a date as a human-readable string
 * @param dateInput - The date to format
 * @param format - Optional format string (defaults to short date/time)
 * @returns Formatted date string
 */
export function formatDate(dateInput: DateInput, format?: string): string {
  const date = normalizeToDate(dateInput);
  
  if (!date) return 'Invalid date';

  const dateTime = DateTime.fromJSDate(date);
  
  if (!dateTime.isValid) return 'Invalid date';

  if (format) {
    return dateTime.toFormat(format);
  }

  return dateTime.toLocaleString(DateTime.DATETIME_SHORT);
}

/**
 * Checks if a date is valid
 * @param dateInput - The date to validate
 * @returns True if the date is valid
 */
export function isValidDate(dateInput: DateInput): boolean {
  const date = normalizeToDate(dateInput);
  return date !== null;
}

/**
 * Calculates the user's age in whole years from a date of birth.
 * Accepts ISO `YYYY-MM-DD` strings as well as any normalizable DateInput.
 * Returns null if the input is missing or invalid.
 */
export function getAgeFromDob(dateInput: DateInput): number | null {
  const date = normalizeToDate(dateInput);
  if (!date) return null;

  const now = new Date();
  let age = now.getFullYear() - date.getFullYear();
  const hasHadBirthdayThisYear =
    now.getMonth() > date.getMonth() ||
    (now.getMonth() === date.getMonth() && now.getDate() >= date.getDate());
  if (!hasHadBirthdayThisYear) age -= 1;
  return age;
}

/**
 * Returns true if the user is strictly under `minAge` years old.
 * Returns false when the dob is missing/invalid (fail-open) so that
 * legacy accounts without a stored dob retain access.
 */
export function isUnderAge(dateInput: DateInput, minAge: number): boolean {
  const age = getAgeFromDob(dateInput);
  if (age === null) return false;
  return age < minAge;
}
